import { useCognitoAuth } from '@/contexts/cognitoAuthContext'
import { PremiumFeatures } from '@/services/types/premiumFeatures/premiumFeatures'
import { usePremiumFeaturesPermissions } from '@/stores/permissions/premiumFeatures'
import { NotificationOutlined } from '@ant-design/icons'
import { clsx } from 'clsx'
import deepEqual from 'deep-equal'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useScript } from 'usehooks-ts'
import s from './BeamerWidget.module.scss'
type BeamerConfig = {
  user_firstname?: string
  user_lastname?: string
  user_email?: string
  user_id?: string
  filter: string
}

export const BEAMER_WIDGET_TEST_ID = 'beamer-widget-test-id'

const BEAMER_WIDGET_ID_SELECTOR = 'beamer-widget'

const DEV_BEAMER_PRODUCT_ID = 'RqSvAYsQ69068'
const PROD_BEAMER_PRODUCT_ID = 'ALSicerf68453'
const BEAMER_PRODUCT_ID = !window.location.href.includes('app.marqii.com')
  ? DEV_BEAMER_PRODUCT_ID
  : PROD_BEAMER_PRODUCT_ID
const ALL_SEGMENT = 'all'

const BASE_BEAMER_CONFIG = {
  product_id: BEAMER_PRODUCT_ID,
  selector: `${BEAMER_WIDGET_ID_SELECTOR}`,
  lazy: true,
}

const BEAMER_WIDGET_URL = 'https://app.getbeamer.com/js/beamer-embed.js'

interface BeamerWidgetProps {
  className?: string
}

export const BeamerWidget = ({ className }: BeamerWidgetProps = {}) => {
  const [hasSetWindow, setHasSetWindow] = useState(false)
  const [hasInitOnce, setHasInitOnce] = useState(false)
  const [beamerConfig, setBeamerConfig] = useState<BeamerConfig | null>(null)

  const { userAttributes } = useCognitoAuth()

  const prevBeamerConfig = useRef<BeamerConfig | null>()

  const premiumFeatures = usePremiumFeaturesPermissions()

  const premiumFeaturesToBeamerFilter = useMemo(() => {
    const isReviewMonitoringEnabled = premiumFeatures.includes(PremiumFeatures.ReviewMonitoring)
    const isSuggestedReviewEnabled = premiumFeatures.includes(
      PremiumFeatures.ManagedReviewsExternal,
    )
    const isManagedReviewEnabled = premiumFeatures.includes(PremiumFeatures.ManagedReviewsInternal)
    const commonFeatures = premiumFeatures.filter(
      (feature) =>
        ![
          PremiumFeatures.ReviewMonitoring,
          PremiumFeatures.ManagedReviewsExternal,
          PremiumFeatures.ManagedReviewsInternal,
        ].includes(feature),
    )

    const reviewFeatures = [
      ...(isReviewMonitoringEnabled && isSuggestedReviewEnabled && isManagedReviewEnabled
        ? [PremiumFeatures.ManagedReviewsInternal, PremiumFeatures.ManagedReviewsExternal]
        : []),
      ...(isReviewMonitoringEnabled && !isSuggestedReviewEnabled && !isManagedReviewEnabled
        ? [PremiumFeatures.ReviewMonitoring]
        : []),
      ...(isReviewMonitoringEnabled && isSuggestedReviewEnabled && !isManagedReviewEnabled
        ? [PremiumFeatures.ManagedReviewsExternal]
        : []),
      ...(isReviewMonitoringEnabled && !isSuggestedReviewEnabled && isManagedReviewEnabled
        ? [PremiumFeatures.ManagedReviewsInternal]
        : []),
    ]

    return [...reviewFeatures, ...commonFeatures]
  }, [premiumFeatures])

  useEffect(() => {
    // object to maybe set as customer beamer config
    const beamerConfigToSet: BeamerConfig = {
      user_email: userAttributes?.email,
      user_id: userAttributes?.sub,
      user_firstname: userAttributes?.given_name,
      user_lastname: userAttributes?.family_name,
      filter: [ALL_SEGMENT, ...premiumFeaturesToBeamerFilter].join(';'),
    }

    if (!deepEqual(prevBeamerConfig.current, beamerConfigToSet)) {
      setHasSetWindow(false)
      setBeamerConfig(beamerConfigToSet)
      prevBeamerConfig.current = beamerConfigToSet
    }
  }, [userAttributes, beamerConfig, premiumFeaturesToBeamerFilter])

  useEffect(() => {
    if (beamerConfig && (!hasSetWindow || !deepEqual(beamerConfig, prevBeamerConfig.current))) {
      window.beamer_config = {
        ...BASE_BEAMER_CONFIG,
        ...beamerConfig,
      }
      setHasSetWindow(true)
    }
  }, [beamerConfig, hasSetWindow])

  const shouldPreventLoad = beamerConfig === null || !hasSetWindow

  const status = useScript(BEAMER_WIDGET_URL, {
    shouldPreventLoad,
  })

  useEffect(() => {
    const isReadyAndWindowSet = status === 'ready' && hasSetWindow

    // first load
    if (!hasInitOnce && beamerConfig !== null && isReadyAndWindowSet) {
      if (window.Beamer) {
        window.Beamer.init()
      }

      setHasInitOnce(true)
    }

    // if user changes (impersonation)
    if (
      hasInitOnce &&
      isReadyAndWindowSet &&
      !deepEqual(beamerConfig, prevBeamerConfig.current) &&
      window.Beamer
    ) {
      window.Beamer.init()
    }
  }, [status, beamerConfig, hasSetWindow, hasInitOnce])
  return hasSetWindow && hasInitOnce ? (
    <div data-testid={BEAMER_WIDGET_TEST_ID} className={clsx(className, s.beamerWrapper)}>
      <NotificationOutlined id={`${BEAMER_WIDGET_ID_SELECTOR}`} />
    </div>
  ) : null
}
